import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { SelectAddressComponent } from '../select-address/select-address.component';

import { ButtonModule } from 'primeng/button';
import { TransportFormService } from 'src/app/trip-calculator/services/transport-form.service';
import { SectionValidationService } from 'src/app/trip-calculator/services/section-validation.service';
import { CommonModule } from '@angular/common';
import { ErrorComponent } from '../../../error/error.component';
import { RoadStopDetailsService } from 'src/app/trip-calculator/services/road-stop-details.service';
import { RoadStopDetails } from 'src/app/trip-calculator/models/road-stop-details';
import {
  catchError,
  debounceTime,
  of,
  startWith,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'app-address-form',
  imports: [
    SelectAddressComponent,
    TranslocoPipe,
    ButtonModule,
    CommonModule,
    ErrorComponent,
  ],
  templateUrl: './address-form.component.html',
})
export class AddressFormComponent implements OnInit, OnDestroy {
  sectionValidationService = inject(SectionValidationService);
  transportFormService = inject(TransportFormService);
  private roadStopDetailsService = inject(RoadStopDetailsService);
  draggedIndex: number | null = null;

  // ── Resolved itinerary, computed live from the addresses in the form ──
  details = signal<RoadStopDetails | null>(null);
  loading = signal(false);
  private itinerarySub?: Subscription;

  /** Total itinerary distance (km), summed across every resolved leg. */
  totalDistance = computed(
    () =>
      this.details()?.itinerary.reduce((sum, step) => sum + step.distance, 0) ??
      null,
  );

  ngOnInit() {
    this.transportFormService.createAddressControls();

    // Recompute the itinerary whenever the addresses change (and once on open).
    // `startWith` seeds the initial calculation since valueChanges is silent on
    // init; the pre-debounce `tap` clears stale distances immediately so we
    // never display legs that are misaligned with the edited rows.
    this.itinerarySub = this.transportFormService.roadStops.valueChanges
      .pipe(
        startWith(null),
        tap(() => this.details.set(null)),
        debounceTime(400),
        switchMap(() => {
          const addresses = this.currentAddresses();
          if (addresses.length < 2 || addresses.some((a) => !a)) {
            this.loading.set(false);
            return of<RoadStopDetails | null>(null);
          }
          this.loading.set(true);
          return this.roadStopDetailsService
            .computeItinerary(addresses)
            .pipe(catchError(() => of<RoadStopDetails | null>(null)));
        }),
      )
      .subscribe((result) => {
        this.details.set(result);
        this.loading.set(false);
      });
  }

  ngOnDestroy() {
    this.itinerarySub?.unsubscribe();
  }

  /** Ordered, trimmed addresses currently entered in the road-stop rows. */
  private currentAddresses(): string[] {
    return (
      this.transportFormService.roadStops.value as { address?: string | null }[]
    ).map((stop) => (stop.address ?? '').trim());
  }

  /** Distance (km) of the leg starting at row `index`, if any. */
  legDistanceAfter(index: number): number | null {
    return this.details()?.itinerary[index]?.distance ?? null;
  }

  /** Translation suffix for the row's label (calculator.label.*), derived from position. */
  labelFor(index: number, isLast: boolean): string {
    if (index === 0) return 'from';
    if (isLast) return 'to';
    return 'waypoint';
  }

  /** Translation suffix for the autocomplete placeholder, derived from position. */
  placeholderFor(index: number, isLast: boolean): string {
    if (index === 0) return 'addressFrom';
    if (isLast) return 'addressTo';
    return 'waypoint';
  }

  // ── Drag & drop: DOM plumbing only; reordering itself lives in the service ──
  onDragStart(event: DragEvent, index: number): void {
    this.draggedIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    if (this.draggedIndex === null) return;
    this.transportFormService.reorderRoadStop(this.draggedIndex, dropIndex);
    this.draggedIndex = null;
  }

  onDragEnd(): void {
    this.draggedIndex = null;
  }
}
